import { api } from './api';

export interface Notification {
  id: number;
  user_id?: number;
  seller_id?: number;
  type: string;
  title: string;
  description: string;
  processed_description?: string;
  client_id?: number;
  related_model_type?: string;
  related_model_id?: number;
  metadata?: Record<string, any>;
  is_completed: boolean;
  completed_at?: string;
  scheduled_date?: string;
  created_at: string;
  updated_at: string;
  icon?: string;
  category?: string;
  redirect_url?: string;
  formatted_date?: string;
  client?: {
    id: number;
    name: string;
    company_name?: string;
  };
}

export interface NotificationCounts {
  not_completed: number;
  completed: number;
  by_category: {
    [key: string]: number;
  };
}

export interface NotificationFilters {
  completed?: boolean;
  category?: string;
  type?: string;
  limit?: number;
}

export class NotificationService {
  static async getNotifications(filters: NotificationFilters = {}): Promise<Notification[]> {
    const params = new URLSearchParams();
    
    if (filters.completed !== undefined) params.append('completed', filters.completed.toString());
    if (filters.category) params.append('category', filters.category);
    if (filters.type) params.append('type', filters.type);
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/notifications?${params.toString()}`);
    return response.data.data || [];
  }

  static async getGroupedNotifications(filters: NotificationFilters = {}): Promise<Record<string, Notification[]>> {
    const params = new URLSearchParams();
    
    if (filters.completed !== undefined) params.append('completed', filters.completed.toString());
    if (filters.category) params.append('category', filters.category);

    const response = await api.get(`/notifications/grouped?${params.toString()}`);
    return response.data.data || {};
  }

  static async getCounts(): Promise<NotificationCounts> {
    const response = await api.get('/notifications/counts');
    return response.data.data;
  }

  static async getNotification(id: number): Promise<Notification> {
    const response = await api.get(`/notifications/${id}`);
    return response.data.data;
  }

  static async createNotification(data: {
    type: string;
    title?: string;
    description: string;
    client_id?: number;
    metadata?: Record<string, any>;
    scheduled_date?: string;
  }): Promise<Notification> {
    const response = await api.post('/notifications', data);
    return response.data.data;
  }

  static async updateNotification(id: number, data: {
    type?: string;
    title?: string;
    description?: string;
    client_id?: number;
    metadata?: Record<string, any>;
    scheduled_date?: string;
  }): Promise<Notification> {
    const response = await api.put(`/notifications/${id}`, data);
    return response.data.data;
  }

  static async markAsCompleted(id: number): Promise<Notification> {
    const response = await api.patch(`/notifications/${id}/complete`);
    return response.data.data;
  }

  static async markAsNotCompleted(id: number): Promise<Notification> {
    const response = await api.patch(`/notifications/${id}/uncomplete`);
    return response.data.data;
  }

  static async deleteNotification(id: number): Promise<void> {
    await api.delete(`/notifications/${id}`);
  }

  static async getTypes(): Promise<Array<{
    value: string;
    name: string;
    title: string;
    icon: string;
    category: string;
  }>> {
    const response = await api.get('/notifications/types');
    return response.data.data || [];
  }
}

